import {
  GridColumnVisibilityModel,
  GridFilterModel,
  GridPaginationModel,
  GridSortModel,
  GridPinnedColumnFields,
} from '@mui/x-data-grid-premium'
import { useRouter } from 'next/router'
import { useMemo, useState } from 'react'
import { useFirstRenderValue } from '@omnea-app/frontend/utils/hooks/useFirstRenderValue'
import { deserializeFilterModel } from '../../utils/serializeFilterModel'
import { transformOmneaFilterModelToMui } from '../../utils/transformMuiGridModels'
import { getTableStateFromLocalStorage } from '../helpers/getTableStateFromLocalStorage'

export interface Values {
  sortModel: GridSortModel
  filterModel: GridFilterModel
  paginationModel: GridPaginationModel
  columnVisibilityModel: GridColumnVisibilityModel
  pinnedColumns: GridPinnedColumnFields
}

type SetState<T> = React.Dispatch<React.SetStateAction<T>>
export interface Setters {
  setSortModel: SetState<GridSortModel>
  setFilterModel: SetState<GridFilterModel>
  setPaginationModel: SetState<GridPaginationModel>
  setColumnVisibilityModel: SetState<GridColumnVisibilityModel>
  setPinnedColumns: SetState<GridPinnedColumnFields>
}

export type GetControlledTableDefaultState = () => Partial<Values>

interface Props {
  tableName: string
  getDefaultState?: GetControlledTableDefaultState
}

export function useControlledTableState(props: Props): [Values, Setters] {
  const { tableName, getDefaultState } = props
  const router = useRouter()

  const defaultState = useFirstRenderValue(getDefaultState)
  const fromFilterModelParam = router.query.fromFilterModel as string | undefined
  const initialState = useMemo(
    () => gridStateToControlledState(tableName, defaultState, fromFilterModelParam),
    [tableName, defaultState, fromFilterModelParam]
  )

  const [sortModel, setSortModel] = useState(initialState.sortModel)
  const [filterModel, setFilterModel] = useState(initialState.filterModel)
  const [paginationModel, setPaginationModel] = useState(initialState.paginationModel)
  const [columnVisibilityModel, setColumnVisibilityModel] = useState(initialState.columnVisibilityModel)
  const [pinnedColumns, setPinnedColumns] = useState(initialState.pinnedColumns)

  const values = useMemo(
    () => ({
      sortModel: sortModel.slice(0, 1), // Sort by only one column
      filterModel,
      paginationModel,
      columnVisibilityModel,
      pinnedColumns,
    }),
    [sortModel, filterModel, paginationModel, columnVisibilityModel, pinnedColumns]
  )
  const setters = useMemo(
    () => ({ setSortModel, setFilterModel, setPaginationModel, setColumnVisibilityModel, setPinnedColumns }),
    []
  )

  return [values, setters]
}

export function getEmptyValues(): Values {
  return {
    sortModel: [],
    filterModel: { items: [] },
    paginationModel: { page: 0, pageSize: 10 },
    columnVisibilityModel: {},
    pinnedColumns: { left: [], right: [] },
  }
}

function gridStateToControlledState(
  tableName: string,
  defaultState: Partial<Values> = {},
  fromFilterModelParam?: string
): Values {
  const stateFromLocalStorage = getTableStateFromLocalStorage(tableName)
  const emptyValues = getEmptyValues()

  const omneaFilterModel = deserializeFilterModel(fromFilterModelParam)
  const muiFilterModel = omneaFilterModel
    ? transformOmneaFilterModelToMui(omneaFilterModel)
    : (defaultState.filterModel ?? emptyValues.filterModel)

  return {
    sortModel: stateFromLocalStorage?.sorting?.sortModel ?? defaultState.sortModel ?? emptyValues.sortModel,
    filterModel: muiFilterModel,
    paginationModel:
      stateFromLocalStorage?.pagination?.paginationModel ?? defaultState.paginationModel ?? emptyValues.paginationModel,
    columnVisibilityModel: {
      ...emptyValues.columnVisibilityModel,
      ...defaultState.columnVisibilityModel,
      ...stateFromLocalStorage?.columns?.columnVisibilityModel,
    },
    pinnedColumns: stateFromLocalStorage?.pinnedColumns ?? defaultState.pinnedColumns ?? emptyValues.pinnedColumns,
  }
}
